import { BottomViewFlyers, FlyerboardPagination, HeaderFixed, PageTitleHelmet } from '@/components';
import { APP_ROUTES, FlyerType, SubscriptionTypeEnum } from '@/config/constants';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import AuthenticationContext from '@/context/AuthenticationContext';
import FlyersContext from '@/context/FlyersContext';
import { NavigationContext } from '@/context/NavigationContext';
import { ContentSwitcherTabPanel, FlyerboardHeader, NavbarSwitcher } from '@/design-system';
import { districtVar, openLoginDrawerVar, subscriptionsVar } from '@/graphql/state';
import { AllFlyerFilters, OwningTypeEnum } from '@/graphql/types';
import { useChildGrades, useEffectOnMount, useURLSearchParams } from '@/hooks';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import scrollToTop from '@/utils/scroolToTop';
import { useReactiveVar } from '@apollo/client';
import { isEmpty, sortBy } from 'lodash';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ExploreAllFlyerboard.styles';

const EXPLORE_ALL_FLYERS_TAB_GROUP_ID = 'explore-all-flyers-flyer-type';

const communityCategoriesDisabled = ['schedules_and_information', 'student_organizations'];
const schoolCategoriesDisabled = ['recreation_and_discovery', 'volunteer_and_local_service'];

const categorySportsMappings = {
  sports: {
    [FlyerType.school]: 'athletics',
  },
  athletics: {
    [FlyerType.community]: 'sports',
  },
};

const MY_SCHOOLS = 'my_schools';

export const ExploreAllFlyerboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthenticationContext);
  const { wasFlyerDetailsPage, isLoading: isUpdatingNav } = useContext(NavigationContext);
  const { grades } = useChildGrades();
  const { audienceId, audienceType, categoryId, categoryName } = useURLSearchParams();
  const district = useReactiveVar(districtVar);
  const districtId = useMemo(() => {
    if (audienceType === 'district') {
      return audienceId;
    }
    return district?.districtId;
  }, [district, audienceId, audienceType]);

  const pageTitle = categoryName || 'All';
  const { track, trackEvent } = useAnalytics();
  const { activeTab, onChangeFlyerType, targetTab } = useContext(FlyersContext);
  const subscriptions = useReactiveVar(subscriptionsVar);
  const trackId = 'all_flyers_page';

  const isDistrictFlyerboard = audienceType === SubscriptionTypeEnum.district;
  const isCommunityTab = activeTab === FlyerType.community;
  const isMySchools = audienceId === 'all';

  const getSubscriptionsIds = useCallback(
    () => subscriptions.map(s => Number(s.id)),
    [subscriptions]
  );

  const trackingEventFrom = useCallback(
    (isTabEvent: boolean = false) => {
      if (categoryId) {
        return EventDetails.viewFlyerSet(isTabEvent ? targetTab : activeTab, categoryId);
      }

      if (audienceId && audienceType) {
        const isMySchools = audienceType === SubscriptionTypeEnum.school && audienceId === 'all';
        const currentAudience = isMySchools ? MY_SCHOOLS : audienceType;
        if (isTabEvent) {
          if (targetTab === SubscriptionTypeEnum.school) {
            if (isMySchools) {
              return EventDetails.viewFlyerSet(MY_SCHOOLS);
            }
            if (audienceType === SubscriptionTypeEnum.district) {
              return EventDetails.viewFlyerSet(SubscriptionTypeEnum.district);
            }
          }

          return EventDetails.viewFlyerSet(targetTab);
        }
        return EventDetails.viewFlyerSet(
          isCommunityTab ? SubscriptionTypeEnum.community : currentAudience
        );
      }
      return '';
    },
    [activeTab, targetTab, categoryId, audienceId, audienceType]
  );

  useEffectOnMount(() => {
    scrollToTop();
  });

  useEffect(() => {
    if (!isUpdatingNav) {
      trackEvent({
        event: TrackingEvents.viewFlyerSet,
        event_detail: trackingEventFrom(),
        stage: TrackingStage.discovery,
        ...(wasFlyerDetailsPage
          ? {
            event_from: TrackingEvents.viewFlyerDetails,
            event_detail_from: EventDetails.viewFlyerDetails(activeTab),
            stage_from: TrackingStage.consideration,
          }
          : {}),
      });
    }
  }, [isUpdatingNav, wasFlyerDetailsPage]);

  const shouldDisableSwitcherTab = useMemo(() => {
    if (categoryId) {
      if (activeTab === FlyerType.school) {
        if (communityCategoriesDisabled.includes(categoryId)) {
          return FlyerType.community;
        }
      } else {
        if (schoolCategoriesDisabled.includes(categoryId)) {
          return FlyerType.school;
        }
      }
    }
    return undefined;
  }, [categoryId, activeTab]);

  const filters = useMemo((): AllFlyerFilters | null => {
    const subscriptionsIds = getSubscriptionsIds();
    if (isAuthenticated && subscriptionsIds.length === 0 || isEmpty(grades)) return null;
    const owningType =
      activeTab === FlyerType.school ? OwningTypeEnum.district : OwningTypeEnum.organization;
    let schoolIds: number[] = [];
    const audienceIdList = isMySchools ? [] : [Number(audienceId)];
    if (isAuthenticated) {
      if (isMySchools || (isDistrictFlyerboard && isCommunityTab)) {
        schoolIds = subscriptionsIds;
      } else {
        if (!isDistrictFlyerboard) {
          schoolIds = audienceIdList;
        }
      }
    } else {
      if (!isDistrictFlyerboard) {
        schoolIds = audienceIdList;
      }
    }
    const allDistrictSchools =
      (isDistrictFlyerboard && !isCommunityTab) ||
      (isCommunityTab && !isAuthenticated && schoolIds.length === 0);

    let baseFilters: AllFlyerFilters = {
      owningType,
      allDistrictSchools,
      schoolIds,
    };

    if (categoryId) {
      const categoryIdFilter = categorySportsMappings[categoryId]?.[activeTab] || categoryId;

      baseFilters = {
        ...baseFilters,
        categories: [categoryIdFilter],
      };
    }
    baseFilters.grades = grades.map(g => g.id);

    return baseFilters;
  }, [audienceId, audienceType, categoryId, activeTab, getSubscriptionsIds, isAuthenticated, grades]);

  const selectedSubscriptions = useMemo(
    () => sortBy(subscriptions, subscription => subscription.name),
    [subscriptions]
  );

  const trackingTabData = useMemo(
    () => ({
      event: TrackingEvents.viewFlyerSet,
      event_detail: trackingEventFrom(true),
      stage: TrackingStage.discovery,
      event_from: TrackingEvents.viewFlyerSet,
      event_detail_from: trackingEventFrom(),
      stage_from: TrackingStage.discovery,
    }),
    [targetTab, activeTab]
  );

  const onBackButtonClick = () => {
    if (isAuthenticated) {
      navigate(`${APP_ROUTES.home}?tab=${activeTab}`);
    } else {
      openLoginDrawerVar(true);
    }
  };

  const handleClickFlyerType = () => {
    track({
      category: `${trackId}:view_all_flyers:bottom_button`,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label: FlyerType.school === activeTab ? 'Community' : 'School',
    });
    onChangeFlyerType(trackingTabData);
    scrollToTop();
  };

  const invertedFlyerType = activeTab === FlyerType.school ? 'community' : 'school';

  // const bottomLabel = categoryId ?
  // `View ${invertedFlyerType} ${categoryName?.toLocaleLowerCase()}`:
  // `View all ${invertedFlyerType} flyers`;

  const bottomLabel = `View all ${invertedFlyerType} flyers`;
  if (!filters) return null;
  return (
    <>
      <PageTitleHelmet>Explore All</PageTitleHelmet>
      <div className={styles.container}>
        <HeaderFixed>
          <NavbarSwitcher
            trackId={trackId}
            navBarTitle={pageTitle}
            onBackButtonClick={onBackButtonClick}
            disabledTab={shouldDisableSwitcherTab}
            trackingData={trackingTabData}
            tabGroupId={EXPLORE_ALL_FLYERS_TAB_GROUP_ID}
          />
          <FlyerboardHeader
            subscriptions={selectedSubscriptions}
            type={activeTab}
            selectedAudienceId={audienceId}
            trackingEventFrom={trackingEventFrom()}
          />
        </HeaderFixed>
        <ContentSwitcherTabPanel
          tabGroupId={EXPLORE_ALL_FLYERS_TAB_GROUP_ID}
          activeTab={activeTab as FlyerType}
        >
          <FlyerboardPagination
            trackId={trackId}
            flyerType={activeTab}
            flyerboardFilters={filters}
            audienceId={audienceId}
            districtId={districtId}
            trackingEventFrom={trackingEventFrom()}
            className={styles.flyerboard}
          />
        </ContentSwitcherTabPanel>
        {categoryId ? (
          <BottomViewFlyers variant={FlyerType[activeTab]} />
        ) : (
          <BottomViewFlyers
            label={bottomLabel}
            variant={FlyerType[activeTab]}
            onClick={handleClickFlyerType}
          />
        )}
      </div>
    </>
  );
};
